import { ref, get, set, push } from 'firebase/database';
import { db } from '../../../integrations/firebase';

/**
 * Sends an automated WhatsApp order update via Interakt.
 * 
 * @param {object} order - The full order object.
 * @param {object} config - Configuration object containing { interaktEnabled, interaktSecret, interaktTemplate }
 * @returns {Promise<{status: string, result?: object}>}
 */
export async function sendOrderUpdate(order, config) {
    if (!config || !config.interaktEnabled) {
        console.log("[Interakt] Service Disabled or Config Missing");
        return { status: "disabled" };
    }

    if (!config.interaktSecret || !config.interaktTemplate) {
        console.error("[Interakt] Missing Secret Key or Template Name");
        return { status: "missing_config" };
    }

    const templateName = config.interaktTemplate;
    const orderId = order.id || order.orderId;

    // Validate required order fields
    if (!order.customer || !order.customer.mobile) {
        console.error("[Interakt] Missing Customer Mobile");
        return { status: "invalid_order_data" };
    }

    // 1. Duplicate Check (Enterprise Rule)
    // interakt_sent / {orderId} / {templateName}
    const duplicateRef = ref(db, `interakt_sent/${orderId}/${templateName.replace(/\./g, '_')}`); // Sanitize template name for FB path
    const duplicateSnap = await get(duplicateRef);

    if (duplicateSnap.exists()) {
        console.warn(`[Interakt] Duplicate Blocked: Order ${orderId}, Template ${templateName}`);
        return { status: "duplicate_blocked" };
    }

    try {
        // 2. Variable Mapping
        // var1: Customer Name
        // var2: Product Names (joined)
        // var3: Amount
        // var4: Payment Method

        const customerName = order.customer.name || 'Customer';

        let productNames = "Order Items";
        if (Array.isArray(order.items)) {
            productNames = order.items.map(i => i.name || i.productName || i.sku).join(", ");
        } else if (order.items && typeof order.items === 'object') {
            // Handle if items is map
            productNames = Object.values(order.items).map(i => i.name || i.productName || i.sku).join(", ");
        }

        // Truncate product names if too long (optional safety)
        if (productNames.length > 50) productNames = productNames.substring(0, 47) + "...";

        const amount = String(order.payment?.total || order.totalAmount || "0");
        const method = (order.payment?.mode || order.paymentMethod || "").toLowerCase() === "cod" ? "COD" : "Prepaid";

        const payload = {
            countryCode: "+91", // Assuming India for now based on context
            phoneNumber: order.customer.mobile.replace(/\D/g, '').slice(-10), // Ensure last 10 digits
            callbackData: orderId,
            type: "template",
            template: {
                name: templateName,
                languageCode: "en",
                bodyValues: {
                    var1: customerName,
                    var2: productNames,
                    var3: amount,
                    var4: method
                }
            }
        };

        console.log("[Interakt] Sending Payload:", JSON.stringify(payload));

        // 3. API Call
        const res = await fetch("https://api.interakt.ai/v1/public/message/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Basic ${config.interaktSecret}` // Ensure Basic Auth format if key is raw, or just key depending on docs. 
                // Docs usually say "Authorization: <SecretKey>" or "Basic <Base64>". 
                // User prompt said "Authorization": config.interaktSecret. We will follow prompt exactly.
            },
            body: JSON.stringify(payload)
        });

        // Handling Auth Header nuance: 
        // If user prompt says "Authorization: config.interaktSecret", we stick to that.
        // But usually it's Basic Base64 encoded.
        // Implementation:
        const headers = {
            "Content-Type": "application/json",
            "Authorization": config.interaktSecret
        };

        // Retrying with detailed fetch for exact control
        const resReal = await fetch("https://api.interakt.ai/v1/public/message/", {
            method: "POST",
            headers: headers,
            body: JSON.stringify(payload)
        });


        const result = await resReal.json();

        // 4. Logging & Success Handling
        const logRef = ref(db, `interakt_logs/${orderId}/${Date.now()}`);
        await set(logRef, {
            status: resReal.ok ? "success" : "failed",
            template: templateName,
            response: result,
            payload: payload
        });

        if (resReal.ok) {
            // Mark as Sent to prevent duplicates
            await set(duplicateRef, {
                sentAt: Date.now(),
                template: templateName,
                amount: amount,
                phone: payload.phoneNumber
            });
            return { status: "success", result };
        } else {
            console.error("[Interakt] API Failure:", result);
            return { status: "failed", result };
        }

    } catch (error) {
        console.error("[Interakt] Exception:", error);
        // Log exception
        const errorLogRef = ref(db, `interakt_logs/${orderId}/${Date.now()}`);
        await set(errorLogRef, {
            status: "exception",
            error: error.message
        });
        return { status: "error", error: error.message };
    }
}
